<template>
  <div
    class="modal"
    :class="{ 'is-active': value }">

    <div class="modal-background"
      @click="close()">
    </div>
    
    <div class="modal-content">
      <div class="modal-card">

        <header class="modal-card-head">
          <p class="modal-card-title">Manage Processing Method Types</p>
          <button
            class="delete"
            aria-label="close"
            @click="close(true)">
          </button>
        </header>

        <div class="modal-card-body">

          <article 
            v-if="!typesLoading && processingTypes.length == 0" 
            class="message is-info is-fullwidth mt-3">
            <div class="message-body">
              There are no processing method types.
            </div>
          </article>

          <table 
            class="table is-fullwidth is-hoverable">

            <thead>
              <tr>
                <th 
                  v-for="(header, headerIndex) in headers" 
                  :key="headerIndex">
                  {{ header.label }}
                </th>
                <th></th>
              </tr>
            </thead>

            <tfoot>
              <tr>
                <td :colspan="headers.length + 1">
                  <div class="centered">
                    <button
                      v-if="!adding"
                      @click="adding = true" 
                      class="button is-primary is-small">
                      Add a new processing method type
                    </button>
                  </div>
                </td>
              </tr>
            </tfoot>

            <tbody>
              <tr
                class="v-centered"
                v-for="type in processingTypes" 
                :key="type.id">

                <template v-if="editingId != type.id">
                  <td>
                    {{ type.processing_type_name }}
                  </td>
                  <td>
                    {{ type.processing_type_desc }}
                  </td>
                </template>

                <template v-else>
                  <td>
                    <v-text-field 
                      required
                      placeholder="Enter Processing Type Name"
                      v-model="type.processing_type_name">
                    </v-text-field>
                  </td>
                  <td>
                    <v-text-field 
                      placeholder="Enter Processing Type Description"
                      v-model="type.processing_type_desc">
                    </v-text-field>
                  </td>
                </template>
                <td>
                  <div class="buttons buttons-right">
                    <template v-if="editingId != type.id">
                      <button
                        @click="editingId = type.id" 
                        class="button is-small">
                        <span class="icon">
                          <i class="fas fa-edit has-text-grey"></i>
                        </span>
                      </button>
                      <button
                        @click="launchConfirmDelete(type.id)" 
                        class="button is-small">
                        <span class="icon">
                          <i class="fas fa-trash has-text-grey"></i>
                        </span>
                      </button>
                    </template>
                    <template v-else>
                      <button
                        @click="update(type)" 
                        class="button is-primary is-small">
                        <span class="icon">
                          <i class="fas fa-save"></i>
                        </span>
                      </button>
                      <button
                        @click="editingId = null" 
                        class="button is-small">
                        <span class="icon">
                          <i class="fas fa-times has-text-grey"></i>
                        </span>
                      </button>
                    </template>
                  </div>
                </td>
              </tr>

              <tr class="v-centered" v-if="adding">
                <td>
                  <v-text-field 
                    required
                    placeholder="Enter Processing Type Name"
                    v-model="type.processing_type_name">
                  </v-text-field>
                </td>
                <td>
                  <v-text-field 
                    placeholder="Enter Processing Type Description"
                    v-model="type.processing_type_desc">
                  </v-text-field>
                </td>
                <td>
                  <div class="buttons buttons-right">
                    <button
                      @click="add" 
                      class="button is-primary is-small">
                      <span class="icon">
                        <i class="fas fa-save"></i>
                      </span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>
    </div>

    <v-confirm-modal 
      v-model="confirmDelete.state"
      :title="confirmDelete.title"
      :message="confirmDelete.message"
      :type="confirmDelete.type"
      @confirm="deleteConfirmedCb ? deleteConfirmedCb() : null"
      @cancel="confirmDelete.state = false"/>

  </div>
</template>

<script>
import VTextField from '@/components/base/VTextField'
import VConfirmModal from '@/components/base/VConfirmModal'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  props: {
    value: {
      type: Boolean
    }
  },
  components: { VTextField, VConfirmModal },
  data() {
    return {
      headers: [
        { label: 'Name' },
        { label: 'Description' }
      ],
      type: {
        processing_type_name: '',
        processing_type_desc: null
      },
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this processing method type?',
        type: 'danger'
      },
      deleteConfirmedCb: null,
      valid: false,
      adding: false,
      editingId: null
    }
  },
  computed: {
    ...mapState("processings", [
      "processingTypes", "typesLoading"
    ])
  },
  watch: {
    value(v) {
      if (!v) {
        this.adding = false;
      }
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("processings", [
      "getProcessingTypes","addProcessingType", "deleteProcessingType", 
      "updateProcessingType", "toggleAllSelected"
    ]),
    ...mapMutations("processings", [
      "selectAll", "deselectAll", "toggleSelected"
    ]),
    initialize() {
      if (this.processingTypes.length == 0) {
        this.getProcessingTypes();
      }
    },
    add() {
      this.addProcessingType(this.type).then(() => {
        this.adding = false;
        this.clearData();
      });
    },
    update(type) {
      this.updateProcessingType(
        { processingTypeId: type.id, data: type }
      ).then(() => {
        this.editingId = null;
      })
    },
    launchConfirmDelete(id) {
      this.deleteConfirmedCb = () => {
        this.deleteProcessingType(id);
        this.confirmDelete.state = false;
      }
      this.confirmDelete.message = 'Are you sure you want to delete this processing method type?';
      this.confirmDelete.state = true;
    },
    close(clear=false) {
      this.$emit('input', false);
      if (clear) {
        this.clearData();
      }
    },
    clearData() {
      Object.assign(this.$data, this.$options.data.call(this));
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';

.table tfoot td .centered {
  display: flex;
  flex-direction: row;
  justify-content: center;
}

.modal-card {
  border-radius: 6px;
}
</style>
